import { useCallback, useMemo, useState } from "react";

import * as ModelConverter from "@/components/features/_model/converter";

import * as DiscountItemOrdinaryShared from "../../_shared";

type InputModel = DiscountItemOrdinaryShared.Model.ModelType.DiscountOrdinaryFormModel["InputModel"];
type ViewModel = DiscountItemOrdinaryShared.Model.ModelType.DiscountOrdinaryFormModel["ViewModel"];
type QueryData = DiscountItemOrdinaryShared.Model.ModelType.DiscountOrdinaryFormModel["QueryData"];
type DiscountOrdinaryInputZodSchemaType =
	DiscountItemOrdinaryShared.Model.ValidationSchema.DiscountOrdinaryInputZodSchemaType;
type ExcludeKeys = DiscountItemOrdinaryShared.Model.ModelType.ExcludeKeys;

export const useEditDiscountOrdinaryFormModel = (initInputModel: InputModel) => {
	/**
	 * DefaultInputModel の初期化
	 */
	const defaultInputModel = useMemo(
		() =>
			DiscountItemOrdinaryShared.Model.Factory.Default.discountOrdinaryInputModelFactory(initInputModel.hotelID),
		[initInputModel.hotelID],
	);

	/**
	 * CurrentInputModel の初期化
	 */
	const [currentInputModel, setCurrentInputModel] = useState<InputModel>(initInputModel);

	/**
	 * ViewModel の初期化
	 */
	const [viewModel, setViewModel] = useState<ViewModel>(
		ModelConverter.inputModelToViewModelConverter<
			InputModel,
			ExcludeKeys,
			QueryData,
			DiscountOrdinaryInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: initInputModel,
			currentInput: initInputModel,
			excludeKeys: DiscountItemOrdinaryShared.Model.ModelType.excludeKeys,
			zodSchema: DiscountItemOrdinaryShared.Model.ValidationSchema.DiscountOrdinaryInputZodSchema,
		}),
	);

	/**
	 * ViewModel を更新する関数。
	 * この関数は useActions でのみ利用される。
	 * @see file://./use-case/use-actions.ts
	 *
	 * DefaultInputModel は ViewModel と比較し、各 Node の変更差分時に利用される。
	 * InitInputModel は ViewModel と lodash.isequal での全体比較時に利用される。
	 * CurrentInputModel は 変更された ViewModel の値を元にバリデーションを実行する際に利用される。
	 * その為、一度 ViewModel -> InputModel に変換し、再度 InputModel -> ViewModel に変換する。
	 */
	const onSetViewModel = useCallback(
		(_viewModel: ViewModel) => {
			const convertedInputModel = ModelConverter.viewModelToInputModelConverter(_viewModel);
			const convertedViewModel = ModelConverter.inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				DiscountOrdinaryInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: initInputModel,
				currentInput: convertedInputModel,
				excludeKeys: DiscountItemOrdinaryShared.Model.ModelType.excludeKeys,
				zodSchema: DiscountItemOrdinaryShared.Model.ValidationSchema.DiscountOrdinaryInputZodSchema,
			});
			// state の更新
			setCurrentInputModel(convertedInputModel);
			setViewModel(convertedViewModel);
		},
		[defaultInputModel, initInputModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel({
			...viewModel,
			isDirty: false,
		});
	}, [viewModel]);

	return {
		inputModel: currentInputModel,
		viewModel,

		onSetViewModel,
		onResetViewModel,
	};
};
